import { useState, useEffect } from 'react';

/**
 * useConnectionStatus
 * Tracks online/offline + approximate quality using the browser Network Information API.
 *
 * Returns:
 *   status  – 'offline' | 'slow' | 'online'
 *   effectiveType – raw navigator.connection.effectiveType if available
 *   online  – boolean (navigator.onLine)
 *   rtt     – round-trip-time in ms if available
 *   downlink – Mbps if available
 */
export const useConnectionStatus = () => {
  const getConn = () =>
    navigator.connection || navigator.mozConnection || navigator.webkitConnection || null;

  const computeStatus = () => {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return 'offline';
    const c = getConn();
    if (!c) return 'online';
    // 2g / slow-2g or explicit saveData, or rtt > 900ms, or downlink < 0.5
    if (c.effectiveType === 'slow-2g' || c.effectiveType === '2g') return 'slow';
    if (typeof c.rtt === 'number' && c.rtt > 900) return 'slow';
    if (typeof c.downlink === 'number' && c.downlink > 0 && c.downlink < 0.5) return 'slow';
    return 'online';
  };

  const snap = () => {
    const c = getConn();
    return {
      status: computeStatus(),
      effectiveType: c?.effectiveType || null,
      online: typeof navigator !== 'undefined' ? navigator.onLine : true,
      rtt: c?.rtt ?? null,
      downlink: c?.downlink ?? null,
      saveData: c?.saveData ?? false,
    };
  };

  const [state, setState] = useState(snap);

  useEffect(() => {
    const update = () => setState(snap());
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    const c = getConn();
    if (c && typeof c.addEventListener === 'function') {
      c.addEventListener('change', update);
    }
    // Lightweight fallback poll for browsers without change events
    const poll = setInterval(update, 15000);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
      if (c && typeof c.removeEventListener === 'function') {
        c.removeEventListener('change', update);
      }
      clearInterval(poll);
    };
  }, []);

  return state;
};

export default useConnectionStatus;
